import JSONbig from 'json-bigint'
import type { SystemSetting } from '~/types/systemSetting'

/**
 * Interface định nghĩa cấu trúc phản hồi từ API cài đặt hệ thống
 * @interface SystemSettingsResponse
 */
interface SystemSettingsResponse {

  /** Dữ liệu phản hồi chứa danh sách cài đặt */
  data: {
    settings: SystemSetting[]
  }

  /** Thông báo từ server */
  message: string
}

/**
 * Store quản lý cài đặt hệ thống
 * @description Quản lý các cài đặt hệ thống, cho phép đọc, cập nhật và xóa cài đặt
 */
export const useSystemSettingsStore = defineStore('systemSettings', {
  /**
   * Khởi tạo state của store
   * @returns {object} State ban đầu của system settings store
   */
  state: () => ({
    /** Danh sách cài đặt hệ thống */
    settings: [] as SystemSetting[],

    /** Trạng thái đang tải dữ liệu */
    isLoading: false,

    /** Thông báo lỗi nếu có */
    error: null as string | null,
  }),

  actions: {
    /**
     * Phân tích giá trị cài đặt từ phản hồi API
     * @description Chuyển đổi giá trị cài đặt từ chuỗi JSON sang đối tượng JavaScript
     * @param {SystemSettingsResponse} response - Phản hồi từ API cài đặt hệ thống
     */
    parseSettingValues(response: SystemSettingsResponse) {
      if (response.data?.settings) {
        for (const setting of response.data.settings) {
          try {
            setting.value = JSONbig.parse(setting.value)
          }
          catch (e) {
            // Giữ nguyên giá trị nếu không phải JSON
          }
          finally {
            setting.type = typeof setting.value === 'string' ? 'string' : 'json'
          }
        }
      }
      this.settings = response.data.settings || []
    },

    /**
     * Lấy danh sách cài đặt hệ thống từ server
     * @description Tải danh sách cài đặt hệ thống từ API
     * @returns {Promise<SystemSettingsResponse>} Phản hồi từ API
     */
    async fetchSystemSettings() {
      this.isLoading = true
      this.error = null

      try {
        const response = await $fetch<SystemSettingsResponse>('/settings', {
          auth: true,
        })

        this.parseSettingValues(response)

        return response
      }
      catch (error: any) {
        this.error = error.message || 'Có lỗi xảy ra khi tải cài đặt hệ thống'
        throw error
      }
      finally {
        this.isLoading = false
      }
    },

    /**
     * Thêm hoặc cập nhật cài đặt hệ thống
     * @description Thêm cài đặt mới hoặc cập nhật cài đặt đã tồn tại
     * @param {SystemSetting} setting - Cài đặt cần thêm hoặc cập nhật
     * @returns {Promise<SystemSettingsResponse>} Phản hồi từ API
     */
    async upsertSystemSetting(setting: SystemSetting) {
      this.isLoading = true
      this.error = null

      try {
        if (typeof setting.value === 'object')
          setting.value = JSON.stringify(setting.value)
        delete setting.type

        const response = await $fetch<SystemSettingsResponse>('/settings', {
          method: 'PUT',
          auth: true,
          body: JSON.stringify(setting),
        })

        await this.fetchSystemSettings()

        return response
      }
      catch (error: any) {
        this.error = error.message || 'Có lỗi xảy ra khi cập nhật cài đặt'
        throw error
      }
      finally {
        this.isLoading = false
      }
    },

    /**
     * Xóa cài đặt hệ thống
     * @description Xóa cài đặt hệ thống theo tên
     * @param {SystemSetting} setting - Cài đặt cần xóa
     * @returns {Promise<SystemSettingsResponse>} Phản hồi từ API
     */
    async deleteSystemSetting(setting: SystemSetting) {
      this.isLoading = true
      this.error = null

      try {
        const response = await $fetch<SystemSettingsResponse>('/settings', {
          method: 'DELETE',
          auth: true,
          body: JSON.stringify({ name: setting.name }),
        })

        await this.fetchSystemSettings()

        return response
      }
      catch (error: any) {
        this.error = error.message || 'Có lỗi xảy ra khi xóa cài đặt'
        throw error
      }
      finally {
        this.isLoading = false
      }
    },
  },

  getters: {
    /**
     * Lấy danh sách tất cả cài đặt hệ thống
     * @returns {SystemSetting[]} Danh sách cài đặt hệ thống
     */
    getSystemSettings(): SystemSetting[] {
      return this.settings
    },

    /**
     * Lấy giá trị cài đặt theo tên
     * @param {string} name - Tên cài đặt cần lấy
     * @returns {any} Giá trị của cài đặt hoặc undefined nếu không tìm thấy
     */
    getSystemSettingFromName: state => (name: string): any => {
      return state.settings.find(setting => setting.name === name)?.value
    },

    /**
     * Kiểm tra có lỗi hay không
     * @returns {boolean} true nếu có lỗi, false nếu không
     */
    hasError(): boolean {
      return !!this.error
    },
  },

  /**
   * Cấu hình lưu trữ dữ liệu
   * @description Lưu trữ state vào localStorage để giữ cài đặt giữa các phiên làm việc
   */
  persist: {
    storage: piniaPluginPersistedstate.localStorage(),
    key: 'systemSettings',
  },
})

/**
 * Cho phép cập nhật Hot Module Replacement cho store
 */
acceptHMRUpdate(useSystemSettingsStore, import.meta.hot)
